import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, ShieldCheckIcon, TargetIcon, UserIcon, ArrowRightIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function AboutPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-331" data-mp="7ii504egp2w">
      <SEOHead title="About Viridia Analytics | Phoenix Marketing Agency | Kevin Rogers, Founder" description="Meet Kevin Rogers, founder of Viridia Analytics. Former shower installer and cabinet finisher turned ad platform insider, building real systems for local service businesses and contractors." canonical="/about" keywords="about Viridia Analytics, Kevin Rogers, Phoenix marketing agency, AI marketing agency, ad platform insider, local business marketing, owner-operated agency" jsonLd={{
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Kevin Rogers',
      jobTitle: 'Founder & CEO',
      worksFor: {
        '@type': 'Organization',
        name: 'Viridia Analytics',
        url: 'https://viridiaanalytics.com'
      },
      description: 'Founder of Viridia Analytics. Former shower installer and cabinet finisher turned ad platform insider, specializing in AI-powered lead conversion and digital advertising for local service businesses and contractors.',
      sameAs: ['https://www.linkedin.com/in/kevinrogers-viridia/'],
      knowsAbout: ['local advertising', 'AI lead conversion', 'digital marketing', 'home services marketing', 'local SEO']
    }} data-id="element-332" data-mp="4wbd2pc2dlq" />
      {/* HERO SECTION */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center overflow-hidden" data-id="element-333" data-mp="3hj2ivzq0ggjq">
        {/* Subtle Grid Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" data-id="element-334" data-mp="3makmkvjf4v8e"></div>

        <ScrollReveal className="relative z-10" data-id="element-335" data-mp="3m6k9bqkk9l8t">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 tracking-tight" data-id="element-336" data-mp="3m9esleru900n">
            Built by an operator,{' '}
            <span className="text-viridia italic" data-id="element-337" data-mp="3d7yqrvf3a681">for operators.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed" data-id="element-338" data-mp="38fx7vqnf1h91">
            Viridia Analytics exists because local service businesses deserve
            better than generic agency promises.
          </p>
        </ScrollReveal>
      </section>

      {/* FOUNDER / LEADERSHIP SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-339" data-mp="2tj67pf5x41id">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-340" data-mp="2phb587c93i30">
          <ScrollReveal data-id="element-341" data-mp="2phumdlu2u2fz">
            <div className="mb-16" data-id="element-342" data-mp="2pjp1gjio30c5">
              <span className="text-viridia font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-343" data-mp="1501xwlfliik3">
                OUR LEADERSHIP
              </span>
              <h2 className="text-4xl md:text-5xl font-bold" data-id="element-344" data-mp="192ymcsrth8vy">
                Driving Growth Through Systems
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-id="element-345" data-mp="1ixqhlpxxfjnk">
            <ScrollReveal direction="right" className="lg:col-span-4" data-id="element-346" data-mp="lccf33pmzsn0">
              <div className="aspect-square rounded-2xl border-4 border-viridia/20 shadow-2xl relative overflow-hidden group" data-id="element-347" data-mp="qa4hwsrtkz5z">
                <img src="https://cdn.magicpatterns.com/uploads/q3DhQsm3VboBpsETP7urnD/Kevindramtichadshot.png" alt="Kevin Rogers - Founder & CEO, Viridia Analytics" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-348" data-mp="v8frd2e7d7no" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" data-id="element-349" data-mp="29925oozikok8"></div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="left" delay={0.2} className="lg:col-span-8" data-id="element-350" data-mp="258s19grde67h">
              <div className="prose prose-lg prose-gray max-w-none" data-id="element-351" data-mp="2j0q3d7mnz1u5">
                <h3 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-352" data-mp="2e6lpe4ezdtjk">
                  Kevin Rogers
                </h3>
                <p className="text-viridia font-semibold text-lg mb-8" data-id="element-353" data-mp="2syw0lbxizjso">
                  Founder & CEO, Viridia Analytics
                </p>

                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-354" data-mp="31xzmeno2udav">
                  Before I ever ran an ad account, I was the guy installing
                  showers and finishing cabinets. I know what it is like to be
                  on a jobsite, covered in dust, watching your phone light up
                  with messages you cannot answer until you are off the truck.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-355" data-mp="3e7p8fyskxtl8">
                  Later, my time working inside the advertising industry at Yelp
                  and Zillow showed me the other half of the equation. I saw
                  exactly where local service businesses were spending ad
                  dollars and exactly where those leads were dying. Most
                  operators were paying for clicks and then losing the job
                  because they could not respond fast enough.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-356" data-mp="30i59z5617ihq">
                  Viridia Analytics exists because most agencies have never
                  swung a hammer, and most contractors have never seen the
                  inside of an ad platform. I built this company to bridge that
                  gap - real systems for HVAC, painters, landscapers, window
                  installers, and other operators, built by someone who has done
                  both jobs.
                </p>
                <div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-100 border-l-4 border-l-viridia" data-id="element-357" data-mp="1xp9j00cjk37v">
                  <p className="text-gray-900 font-medium italic m-0" data-id="element-358" data-mp="1sr80phmxw7xg">
                    "Every client gets my direct attention. No account managers,
                    no runaround."
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* VALUES SECTION */}
      <section className="py-24 bg-viridia-black" data-id="element-359" data-mp="2cdxb1wn6hut2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-360" data-mp="2cf8etwgmj2k6">
          <ScrollReveal data-id="element-361" data-mp="27kkzoo7ifki9">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-16 text-center" data-id="element-362" data-mp="27k1ff0ue0k12">
              Why Viridia?
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-363" data-mp="303i5bbos8ehn">
            <ScrollReveal delay={0.1} data-id="element-364" data-mp="2vc04qoqo8fx7">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 h-full" data-id="element-365" data-mp="2vayj5qc14b18">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6" data-id="element-366" data-mp="32o6eh5i1dti9">
                  <MapPinIcon className="w-7 h-7 text-viridia" data-id="element-367" data-mp="37mhophc8u610" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-368" data-mp="3ck9gmbcw1s57">
                  Phoenix Local
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-369" data-mp="3gnz22ry9ntm0">
                  We're based in Phoenix and specialize in the local market. We
                  understand the seasonality, competition, and customer behavior
                  unique to Arizona service businesses.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-370" data-mp="44zfp6kqisz0">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 h-full" data-id="element-371" data-mp="43xy0j97r88b">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6" data-id="element-372" data-mp="885p2n35909p">
                  <ShieldCheckIcon className="w-7 h-7 text-viridia" data-id="element-373" data-mp="d6gt2h6rcgk0" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-374" data-mp="yfkwfeku2731">
                  Platform Insiders
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-375" data-mp="tjvoonyntjvn">
                  Our team includes former employees from major ad platforms. We
                  don't guess - we know exactly how the algorithms work and how
                  to maximize your spend.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-376" data-mp="arjq8cb4n2qs">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 h-full" data-id="element-377" data-mp="asmiooo3ld8t">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6" data-id="element-378" data-mp="pgzdvjfd2s29">
                  <TargetIcon className="w-7 h-7 text-viridia" data-id="element-379" data-mp="knwkbbal3im2" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-380" data-mp="3t3zq1bi4yx7r">
                  No Wasted Leads
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-381" data-mp="3o5xxlzytlljm">
                  Most agencies stop at getting you clicks. We build the entire
                  system - from ad to answer to booked job - so no lead falls
                  through the cracks.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4} data-id="element-382" data-mp="1yp2co3ifd3rd">
              <div className="bg-viridia-surface p-8 rounded-2xl border border-gray-800 h-full" data-id="element-383" data-mp="1yq40l8kjhkqi">
                <div className="w-14 h-14 bg-viridia/10 rounded-xl flex items-center justify-center mb-6" data-id="element-384" data-mp="2mfojjrgp0hzs">
                  <UserIcon className="w-7 h-7 text-viridia" data-id="element-385" data-mp="2rcezmeshzdl7" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-386" data-mp="2deqd72is7ye1">
                  Owner-Operated
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-387" data-mp="2he9209dq4tm5">
                  You work directly with the founder. No layers of account
                  managers. Fast decisions, real accountability, and someone who
                  actually cares about your results.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WHY CLIENTS TRUST US */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-388" data-mp="1vlvpcd26emnt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-389" data-mp="1huh8abrtwvje">
          <ScrollReveal data-id="element-390" data-mp="1lwc636ajwqsf">
            <div className="text-center max-w-3xl mx-auto" data-id="element-391" data-mp="1lvsqhw8diehk">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-392" data-mp="17xchtzqyadws">
                Why Clients Trust Us
              </h2>
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 md:p-10" data-id="element-393" data-mp="1cwfn9lauem5a">
                <p className="text-xl text-gray-300 leading-relaxed" data-id="element-394" data-mp="yx69esa4044h">
                  Our work is documented. See how{' '}
                  <Link to="/case-studies/tfp-landscaping" className="text-blue-400 font-semibold hover:text-blue-300 transition-colors" data-id="element-395" data-mp="12yrkc83328c1">
                    TFP Landscaping increased leads by 284%
                  </Link>{' '}
                  or how{' '}
                  <Link to="/case-studies/finish-coat-painting" className="text-orange-500 font-semibold hover:text-orange-400 transition-colors" data-id="element-396" data-mp="loxb6o362z2h">
                    Finish Coat Painting closed $15.05k from AI bookings
                  </Link>
                  .
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* MISSION SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-397" data-mp="10cidifs7wu9c">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-398" data-mp="1edclwyf9mv80">
          <ScrollReveal data-id="element-399" data-mp="1eb8kqo8r4abf">
            <TargetIcon className="w-12 h-12 text-viridia mx-auto mb-8 opacity-50" data-id="element-400" data-mp="19ehw3civs6ee" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-8" data-id="element-401" data-mp="19cf4io22y57k">
              "We built Viridia to solve one problem: local businesses spend
              money on ads, then lose the leads. Our job is to make sure that
              stops happening."
            </h2>
            <p className="text-lg font-semibold text-gray-500 uppercase tracking-widest" data-id="element-402" data-mp="1f4ta3zwip9je">
              - Kevin Rogers, Founder
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia to-viridia-dark relative overflow-hidden" data-id="element-403" data-mp="1t1zm9bmqk9zn">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-404" data-mp="1y1uh1q9mtjpz"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-405" data-mp="22ykvk95sg79a">
          <ScrollReveal data-id="element-406" data-mp="27rnpwjie3kqj">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-407" data-mp="27ti1v8boo2ih">
              Let's Build Something Together
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-408" data-mp="2gslkkb6xufwg">
              Whether you need ads, AI lead conversion, or a complete growth
              system - let's talk.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-white text-viridia-dark hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1" data-id="element-409" data-mp="a3tn26a81i26">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-410" data-mp="3sa81s7gbdw1m" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}